import { Level, reportError } from "@/lib/owl";
import React, {
  useEffect,
  useRef,
  useCallback,
  ReactNode,
  isValidElement,
  cloneElement,
} from "react";

interface ExposureProps {
  children: ReactNode;
  onExpose?: () => void;
  threshold?: number | number[];
  rootMargin?: string;
  once?: boolean;
  disabled?: boolean;
  [key: string]: any;
}

/**
 * 检查 IntersectionObserver 是否可用
 */
const isIntersectionObserverSupported = typeof window !== "undefined" && "IntersectionObserver" in window;

const Exposure = ({
  children,
  onExpose,
  threshold = 0.1,
  rootMargin = "0px",
  ...props
}: ExposureProps) => {
  const targetRef = useRef<HTMLElement | null>(null);
  const exposedRef = useRef(false);
  const scrollHandlerRef = useRef<(() => void) | null>(null);

  const handleExpose = useCallback(() => {
    if (exposedRef.current) return;
    exposedRef.current = true;
    onExpose?.();
  }, [onExpose]);

  // 降级方案：使用 scroll 事件监听
  const handleScroll = useCallback(() => {
    try {
      if (!targetRef.current || exposedRef.current) return;

      const rect = targetRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;

      // 检查元素是否在视口内（纵向 + 横向）
      const thresholdValue = Array.isArray(threshold) ? threshold[0] : threshold;

      // 纵向可见比例计算
      const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
      const elementHeight = rect.height;
      const verticalRatio = elementHeight > 0 ? visibleHeight / elementHeight : 0;

      // 横向可见比例计算
      const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);
      const elementWidth = rect.width;
      const horizontalRatio = elementWidth > 0 ? visibleWidth / elementWidth : 0;

      // 元素需要在纵向和横向上都满足阈值条件才算可见
      const isInViewport =
        rect.top < windowHeight &&
        rect.bottom > 0 &&
        rect.left < windowWidth &&
        rect.right > 0;

      if (verticalRatio >= thresholdValue && horizontalRatio >= thresholdValue && isInViewport) {
        handleExpose();
        // 移除滚动监听
        if (scrollHandlerRef.current) {
          window.removeEventListener("scroll", scrollHandlerRef.current);
        }
      }
    } catch (error) {
      reportError({
        name: '[Exposure] handleExposeError',
        msg: error,
        category: 'jsError',
        level: Level.ERROR,
      })
    }
  }, [threshold, handleExpose]);

  useEffect(() => {
    if (exposedRef.current) {
      return;
    }

    // 如果 IntersectionObserver 可用，使用原生 API
    if (isIntersectionObserverSupported) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              handleExpose();

              if (targetRef.current) {
                observer.unobserve(targetRef.current);
              }
            }
          });
        },
        {
          threshold,
          rootMargin,
        }
      );

      if (targetRef.current) {
        observer.observe(targetRef.current);
      }

      return () => {
        observer.disconnect();
      };
    } else {
      // 降级方案：使用定时检查（兼容内部滚动容器）
      scrollHandlerRef.current = handleScroll;

      // 监听任意滚动容器的滚动：使用 requestAnimationFrame 定时检查曝光情况
      let rafId: number | null = null;
      let lastCheckTime = 0;
      const CHECK_INTERVAL = 200; // 每 200ms 检查一次

      const checkVisibility = (timestamp: number) => {
        if (timestamp - lastCheckTime >= CHECK_INTERVAL) {
          lastCheckTime = timestamp;
          handleScroll();
        }
        if (!exposedRef.current) {
          rafId = requestAnimationFrame(checkVisibility);
        }
      };

      // 同时监听 window 的 scroll 事件（作为优化，滚动时立即检查）
      window.addEventListener("scroll", handleScroll, { passive: true });

      // 启动定时检查
      rafId = requestAnimationFrame(checkVisibility);

      // 初始检查一次
      handleScroll();

      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        if (scrollHandlerRef.current) {
          window.removeEventListener("scroll", scrollHandlerRef.current);
        }
      };
    }
  }, [threshold, rootMargin, handleExpose, handleScroll]);

  // 处理 children 可能不是 ReactElement 的情况
  if (!isValidElement(children)) {
    return React.createElement(
      "div",
      {
        ...props,
        ref: targetRef,
      },
      children
    );
  }

  // 对于 ReactElement，处理 ref 转发
  const element = children as React.ReactElement & {
    ref?: React.Ref<HTMLElement>;
  };
  const originalRef = element.ref;

  // 创建合并的 ref 回调
  const mergedRef = useCallback(
    (element: HTMLElement | null) => {
      targetRef.current = element;

      // 处理原始 ref
      if (element && originalRef) {
        if (typeof originalRef === "function") {
          originalRef(element);
        } else if (originalRef) {
          originalRef.current = element;
        }
      }
    },
    [originalRef]
  );

  // 使用类型断言来避免 cloneElement 的类型错误
  return cloneElement(element, {
    ...props,
    ref: mergedRef,
  } as any);
};

export default Exposure;
